import React, { useEffect, useState, type JSX } from "react";
import { cn, createInCartHandler } from "@/utils/utils";
import type { CollectionWithPrice } from "@/types/api";
import { AlertCircle, Plus, ShoppingCart } from "lucide-react";
import { useCart } from "../contexts/cart-context";
import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/use-mobile";
import { useProducts } from "../contexts/product-context";

export type CollectionVariant = "marketplace" | "in-cart";

export interface CollectionProps
  extends React.HTMLAttributes<HTMLDivElement> {
  item: CollectionWithPrice;
  inCart?: boolean;
  onClick?: () => void;
  variant: CollectionVariant;
}

const renderButtonByVariant: Record<
  CollectionVariant,
  (args: { incart: boolean; onClick: (e: React.MouseEvent) => void, className?: string }) => JSX.Element
> = {
  marketplace: ({ incart, onClick, className }) => (
    <Button
      className={cn(
        "h-8 rounded-full flex items-center justify-center px-3 gap-2",
        "text-sm font-medium text-gray-500",
        "transition-colors duration-300",
        (incart ? "bg-musio-red text-musio-light-lilac hover:bg-musio-red/90"
          : "bg-musio-green text-musio-mid-gray hover:bg-musio-green/90"),
        "cart-button",
        className,
      )}
      onClick={onClick}
      role="button"
      id="cart-button"
      aria-label={incart ? "Remove From Cart" : "Add To Cart"}
    >
      <ShoppingCart
        className={cn(
          "w-5 h-5 transition-transform duration-300",
          incart ? "text-musio-light-lilac" : "text-musio-mid-gray"
        )}
      />
      <span>{incart ? "Remove From Cart" : "Add To Cart"}</span>
    </Button>
  ),

  "in-cart": ({ incart, onClick, className }) => (
    <Button
      className={cn(
        "w-8 h-8 rounded-full flex items-center justify-center",
        "text-xl font-bold text-gray-500",
        "transition-colors duration-300",
        (incart ? "bg-musio-red hover:bg-musio-red/90"
          : "bg-musio-green hover:bg-musio-green/90"),
        "cart-button",
        className,
      )}
      id="cart-button"
      onClick={onClick}
      role="button"
      aria-label={incart ? "Remove From Cart" : "Add To Cart"}
    >
      <Plus
        className={cn(
          "w-5 h-5 transition-transform duration-300",
          incart ? "-rotate-45 text-musio-light-lilac"
            : "rotate-0 text-musio-mid-gray"
        )}
      />
    </Button>
  ),
};

export const Collection: React.FC<CollectionProps> = ({
  item,
  className,
  onClick,
  variant,
  ...props
}) => {
  const ctx = useCart();
  const isMobile = useIsMobile();
  const { isPriceIdsFetching, isPriceError } = useProducts();

  const [incart, setIncart] = useState(() => ctx.cart.includes(item));
  const [highlight, setHighlight] = useState(false);

  const handleAddToCart = createInCartHandler(item, incart, setIncart, ctx);

  useEffect(() => {
    setIncart(ctx.cart.includes(item));
  }, [ctx.cart, item]);

  const handleRowClick = () => {
    setHighlight((prev) => !prev);
    onClick?.();
  };

  return (
    <div
      className={cn(
        "flex flex-col items-center cursor-pointer select-none w-full overflow-hidden",
        "bg-[#00000040] hover:bg-[#aaaaaa20] has-[.cart-button:hover]:bg-[#00000040] has-[.trial-link:hover]:bg-[#00000040]",
        "sm:flex-row",
        highlight && variant !== "in-cart" ? "border-white border-1 border-solid border-r-7" : "",
        className
      )}
      onClick={handleRowClick}
      {...props}
    >
      <div className="
        flex flex-row-reverse justify-between w-full gap-2 overflow-hidden 
        sm:justify-normal sm:flex-row sm:items-center sm:flex-1 sm:min-w-0
      "
      >
        <div className="w-[100px] h-[60px] flex-shrink-0 sm:w-[142px] sm:h-[80px]">
          <img
            className="w-full h-full object-cover"
            src={item.cartItem?.productImage} // WARN: NEED TO FIGURE THIS OUT 
            alt={item.cartItem?.productName}
          />
        </div>
        <div className="flex-1 min-w-0 p-2 sm:p-2 sm:flex-shrink">
          <h2
            className="
              font-bold text-musio-light-lilac
              line-clamp-2
              sm:px-0 md:p-0
            "
          >
            {item.cartItem?.productName}
          </h2>
        </div>
      </div>

      <div className="flex flex-row items-center justify-between w-full gap-1 p-4 overflow-hidden sm:justify-end sm:w-auto sm:gap-2 sm:flex-shrink-0">
        <div className="inline-flex items-center gap-1 min-w-0 flex-shrink sm:gap-2 sm:flex-shrink-0 relative">
          {isPriceError ?
            <div className="flex items-center gap-2 text-musio-red italic">
              <AlertCircle />Error getting price
            </div>
            : isPriceIdsFetching ? (
              <div className="flex items-center gap-2">
                <div className="h-7 w-20 rounded bg-slate-700 animate-pulse" />
              </div>
            ) : (
              <>
                {item.cartItem?.salePriceActive && (
                  <span className="relative inline-block flex-shrink-0">
                    <p className="text-xl font-normal text-lilac whitespace-nowrap">
                      {item.cartItem?.formattedPrice}
                    </p>
                    <span
                      className="absolute left-0 top-1/2 w-full h-0.5 bg-red-500 rotate-[-10deg] pointer-events-none"
                      style={{ transform: "rotate(-10deg)", top: "50%" }}
                    />
                  </span>
                )}
                <p
                  className={cn(
                    "text-[24px] font-normal whitespace-nowrap",
                    item.cartItem?.salePriceActive ? "text-musio-green" : "text-lilac"
                  )}
                >
                  {item.cartItem?.salePriceActive ? item.cartItem?.formattedSalePrice : item.cartItem?.formattedPrice}
                </p>
              </>
            )}
        </div>

        {isPriceIdsFetching || isPriceError ? null :
          <div className="flex-shrink-0">
            {renderButtonByVariant[isMobile ? "in-cart" : variant]({
              incart,
              onClick: handleAddToCart,
            })}
          </div>
        }
      </div>

    </div >
  );
};

